/**
 * 마크다운 후처리 함수들
 * HTML 생성 후 마진 조정 등의 후처리를 담당합니다.
 */

import { restoreInlineCode } from './preprocessors';

// TailwindCSS 소수점 클래스(mb-2.5 등)와 임의값 클래스도 포함되도록 정규식 확장
const MARGIN_BOTTOM_REGEX = /mb-(?:\d+(?:\.\d+)?|\[[^\]]+\])/;

/**
 * 마지막 문단의 마진 바텀을 제거하는 함수
 * DOM 파서를 사용하여 HTML 구조를 분석하고 마진을 조정합니다.
 */
export const replaceLastMargin = (htmlStr: string): string => {
	// DOM 파서를 사용하여 모든 태그를 순회
	const parser = new DOMParser();
	const doc = parser.parseFromString(htmlStr, 'text/html');

	let hasChanges = false;

	// HR 태그 위에 있는 요소들의 마진 바텀 제거 (HR의 상단 마진과 중복 방지)
	const hrElements = doc.getElementsByTagName('hr');
	for (const hrElement of Array.from(hrElements)) {
		const prevElement = hrElement.previousElementSibling as HTMLElement;
		if (prevElement?.className && MARGIN_BOTTOM_REGEX.test(prevElement.className)) {
			const prevClass = prevElement.className.match(MARGIN_BOTTOM_REGEX)?.[0];
			if (prevClass) {
				prevElement.classList.replace(prevClass, 'mb-0');
				hasChanges = true;
			}
		}
	}

	// p 다음에 ul/ol이 오는 경우 p의 마진 바텀 제거
	const paragraphElements = doc.getElementsByTagName('p');
	for (const paragraphElement of Array.from(paragraphElements)) {
		const parentElement = paragraphElement.parentElement as HTMLElement | null;
		if (!parentElement) continue;
		if (parentElement.tagName.toLowerCase() !== 'li') continue;

		const nextElement = paragraphElement.nextElementSibling as HTMLElement | null;
		if (!nextElement) continue;

		const tagName = nextElement.tagName.toLowerCase();
		if (tagName !== 'ul' && tagName !== 'ol') continue;
		if (nextElement.parentElement !== parentElement) continue;

		if (paragraphElement.className && MARGIN_BOTTOM_REGEX.test(paragraphElement.className)) {
			const pClass = paragraphElement.className.match(MARGIN_BOTTOM_REGEX)?.[0];
			if (pClass) {
				paragraphElement.classList.replace(pClass, 'mb-0');
				hasChanges = true;
			}
		}
	}

	// ul 내부에 중첩된 ul의 마진 바텀 제거 및 마지막 li 처리
	const ulElements = doc.getElementsByTagName('ul');
	for (const ulElement of Array.from(ulElements)) {
		const isNestedInUl = ulElement.parentElement?.tagName.toLowerCase() === 'li';

		if (isNestedInUl && ulElement.className && MARGIN_BOTTOM_REGEX.test(ulElement.className)) {
			const ulClass = ulElement.className.match(MARGIN_BOTTOM_REGEX)?.[0];
			if (ulClass) {
				ulElement.classList.replace(ulClass, 'mb-0');
				hasChanges = true;
			}
		}

		const lastLi = Array.from(ulElement.children)
			.reverse()
			.find(el => el.tagName.toLowerCase() === 'li') as HTMLElement;
		if (lastLi?.className && MARGIN_BOTTOM_REGEX.test(lastLi.className)) {
			const liClass = lastLi.className.match(MARGIN_BOTTOM_REGEX)?.[0];
			if (liClass) {
				lastLi.classList.replace(liClass, 'mb-0');
				hasChanges = true;
			}
		}
	}

	// 전체 문서에서 가장 마지막에 위치한 요소의 마진 바텀 제거
	const lastElement = doc.body.lastElementChild as HTMLElement;
	if (lastElement?.className && MARGIN_BOTTOM_REGEX.test(lastElement.className)) {
		const lastClass = lastElement.className.match(MARGIN_BOTTOM_REGEX)?.[0];
		if (lastClass) {
			lastElement.classList.replace(lastClass, 'mb-0');
			hasChanges = true;
		}
	}

	return hasChanges ? doc.body.innerHTML : htmlStr;
};

/**
 * HTML 후처리 파이프라인
 * 1. 마진 처리
 * 2. 코드 토큰 복원
 */
export const processHtml = (htmlStr: string): string => {
	// 1. 마진 처리
	const marginProcessed = replaceLastMargin(htmlStr);
	// 2. 코드 토큰 복원 (HTML 구조가 안정된 상태에서 복원)
	return restoreInlineCode(marginProcessed);
};
